import { createStore, updateStore, useLegalEntities, useStore, useStoreForm } from '@gozoki/api';
import { COUNTRIES, STORE_TYPES } from '@gozoki/api-types';
import { generateRandomRef, getCountryLabel } from '@gozoki/tools';
import { Button, Checkbox, FormControlLabel, IconButton, Typography } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation } from '@tanstack/react-query';
import { useToast } from '../../components/communication/Toast';
import LoadingPaper from '../../components/containers/LoadingPaper';
import DatePickerRhf from '../../components/inputs/DatePickerRhf';
import SelectRhf from '../../components/inputs/SelectRhf';
import TextFieldRhf from '../../components/inputs/TextFieldRhf';
import Page from '../../components/Page';
import { ROUTES } from '../../utils/constants/routes.constants';
import { getErrorMessageWithSentry } from '../../utils/sentry';
import { ThemeColors } from '../../utils/theme/colors.theme';
import { fonts } from '../../utils/theme/fonts.theme';
import { ButtonOverrides } from '../../utils/theme/overrides.theme';
import { getStoreTypeLabel } from '../../utils/types/toFrench';

const AddStorePage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const updateMode = id !== undefined; // Update mode if there is a promo campaign ID in the URL

    // If this is update mode, use isLoading to show a loading indicator before the data arrives
    const { data, isLoading, refetch: refetchStore } = useStore(parseInt(id ?? '', 10));

    const { control, trigger, getValues, setValue, register, watch } = useStoreForm();

    const { data: legalEntities = [] } = useLegalEntities();

    const [Toast, showToast] = useToast();

    // Fill the form with initial data once the data is fetched
    useEffect(() => {
        // Set all values
        if (data) {
            setValue('checkpointStoreRef', data.checkpointStoreRef);
            setValue('label', data.label);
            setValue('address', data.address.address);
            setValue('addressAdditionnal', data.address.addressAdditionnal ?? null);
            setValue('city', data.address.city);
            setValue('postalCode', data.address.postalCode);
            setValue('country', data.address.country);
            setValue('storeType', data.storeType);
            setValue('isOnline', data.isOnline);
            setValue('bearcodName', data.bearcodName);
            setValue('legalEntityId', data.legalEntityId);
            setValue('siret', data.siret);
            setValue('openingDate', data.openingDate);
            setValue('quantityFactor', data.quantityFactor);
            setValue('growthFactor', data.growthFactor);
        } else {
            setValue('checkpointStoreRef', generateRandomRef(10));
        }
    }, [setValue, data]);

    const navigateBack = useCallback(() => {
        refetchStore();
        navigate(ROUTES.STORES);
    }, [navigate, refetchStore]);

    // ******************************************************************** //
    //                              MUTATIONS                               //
    // ******************************************************************** //

    const { mutate: upload, isPending: isUploading } = useMutation({
        mutationFn: createStore,
        onSuccess: navigateBack,
        onError: (error) => {
            showToast({
                severity: 'error',
                message: getErrorMessageWithSentry(error),
            });
        },
    });

    const { mutate: update, isPending: isUpdating } = useMutation({
        mutationFn: updateStore,
        onSuccess: navigateBack,
        onError: (error) => {
            showToast({
                severity: 'error',
                message: getErrorMessageWithSentry(error),
            });
        },
    });

    // ******************************************************************** //
    //                               HANDLERS                               //
    // ******************************************************************** //

    const uploadStore = async () => {
        const valid = await trigger(); // Trigger form validation

        if (valid) {
            upload(getValues());
        }
    };

    const handleUpdateStore = async () => {
        const valid = await trigger(); // Trigger form validation

        if (valid) {
            update({
                id: parseInt(id!, 10),
                data: getValues(),
            });
        }
    };

    const isOnlineValue = watch('isOnline');
    const createShippingValue = watch('createShipping');
    const storeTypeValue = watch('storeType');

    return (
        <Page sx={{ maxWidth: '50vw', margin: 'auto' }}>
            <Toast />
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <IconButton onClick={navigateBack}>
                    <ArrowBackIcon
                        sx={{
                            color: ThemeColors.BLACK,
                            height: '24px',
                            width: '24px',
                        }}
                    />
                </IconButton>
                <Typography style={{ ...fonts.pageSubtitle, marginLeft: '16px' }} flexGrow={1}>
                    {updateMode ? 'Modifier une boutique' : 'Ajouter une boutique'}
                </Typography>

                <Button
                    variant="outlined"
                    sx={{ marginRight: '16px', ...ButtonOverrides.blackOutlined }}
                    onClick={navigateBack}
                >
                    Annuler
                </Button>
                <Button
                    variant="contained"
                    disabled={isUpdating || isUploading}
                    onClick={updateMode ? handleUpdateStore : uploadStore}
                >
                    {updateMode ? 'Modifier' : 'Ajouter'}
                </Button>
            </div>
            <LoadingPaper
                sx={{ marginTop: '40px', padding: '24px', paddingBottom: '40px' }}
                loading={isLoading && updateMode}
            >
                <Typography mb="8px" style={fonts.inputHeader}>
                    Référence
                </Typography>
                <TextFieldRhf
                    variant="outlined"
                    placeholder="Ref"
                    color="info"
                    control={control}
                    trigger={trigger}
                    name="checkpointStoreRef"
                    fullWidth
                    disabled
                />
                <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                    Nom
                </Typography>
                <TextFieldRhf
                    variant="outlined"
                    placeholder="Nom de la boutique"
                    color="info"
                    control={control}
                    trigger={trigger}
                    name="label"
                    fullWidth
                />
                <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                    Adresse
                </Typography>
                <TextFieldRhf
                    variant="outlined"
                    placeholder="Adresse"
                    color="info"
                    control={control}
                    trigger={trigger}
                    name="address"
                    fullWidth
                />
                <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                    Adresse additionnelle
                </Typography>
                <TextFieldRhf
                    variant="outlined"
                    placeholder="Adresse"
                    color="info"
                    control={control}
                    trigger={trigger}
                    name="addressAdditionnal"
                    fullWidth
                />
                <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                    Ville
                </Typography>
                <TextFieldRhf
                    variant="outlined"
                    placeholder="Adresse"
                    color="info"
                    control={control}
                    trigger={trigger}
                    name="city"
                    fullWidth
                />
                <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                    Code postal
                </Typography>
                <TextFieldRhf
                    variant="outlined"
                    placeholder="Adresse"
                    color="info"
                    control={control}
                    trigger={trigger}
                    name="postalCode"
                    fullWidth
                />
                <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                    Pays
                </Typography>
                <SelectRhf
                    control={control}
                    name="country"
                    options={COUNTRIES.map((country) => ({
                        value: country,
                        label: getCountryLabel(country),
                    }))}
                />
                <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                    Référence Bearcod
                </Typography>
                <TextFieldRhf
                    variant="outlined"
                    placeholder="Référence Bearcod"
                    color="info"
                    control={control}
                    trigger={trigger}
                    name="bearcodName"
                    fullWidth
                />

                <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                    Type de boutique
                </Typography>
                <SelectRhf
                    control={control}
                    name="storeType"
                    options={STORE_TYPES.map((type) => ({
                        value: type,
                        label: getStoreTypeLabel(type),
                    }))}
                />

                <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                    Entreprise Légal
                </Typography>

                <SelectRhf
                    control={control}
                    name="legalEntityId"
                    options={legalEntities.map((entity) => ({
                        value: entity.id,
                        label: entity.name,
                    }))}
                />

                <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                    SIRET
                </Typography>
                <TextFieldRhf
                    variant="outlined"
                    placeholder="SIRET (14 chiffres)"
                    color="info"
                    control={control}
                    trigger={trigger}
                    name="siret"
                    fullWidth
                />

                <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                    Statut
                </Typography>
                <FormControlLabel
                    control={<Checkbox {...register('isOnline')} checked={isOnlineValue} />}
                    label="Boutique en ligne"
                />

                <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                    Date d'ouverture
                </Typography>
                <DatePickerRhf control={control} name="openingDate" trigger={trigger} />

                <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                    Facteur de quantité pour l'ouverture (0.8 si c'est un petit magasin, 1.2 si
                    c'est un grand magasin)
                </Typography>
                <TextFieldRhf
                    variant="outlined"
                    placeholder="Facteur de quantité"
                    color="info"
                    control={control}
                    trigger={trigger}
                    name="quantityFactor"
                    type="number"
                    inputProps={{
                        min: 0,
                        max: 2,
                        step: 0.1,
                    }}
                    fullWidth
                />
                <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                    Taux de croissance
                </Typography>
                <TextFieldRhf
                    variant="outlined"
                    placeholder="Taux de croissance"
                    color="info"
                    control={control}
                    trigger={trigger}
                    name="growthFactor"
                    type="number"
                    inputProps={{
                        min: 0,
                        max: 2,
                        step: 0.1,
                    }}
                    fullWidth
                />
                {!updateMode && (
                    <>
                        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                            Action supplémentaire
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    {...register('createShipping')}
                                    checked={createShippingValue}
                                    disabled={storeTypeValue !== 'BOUTIQUE'}
                                />
                            }
                            label="Créer automatiquement un stock transport associé"
                        />
                    </>
                )}
            </LoadingPaper>
        </Page>
    );
};

export default AddStorePage;
